import React, { useEffect, useReducer, useRef, useState } from 'react'

import ArrowButton from 'steps/ArrowButton3'
import IntersectionVisible from 'components/IntersectionVisible'
import LockedStep from 'steps/LockedStep'
import SkipButton from 'steps/SkipButton'
import StartSlideWrapper from 'steps/StartSlideWrapper'
import { puckHasOwnStartScreen, stepHasPuckContent } from './puck/puckUtils'
import { StepService, UNTRACKED_STATUS } from 'services/common'
import BreakHTMLFooter from './BreakHTMLFooter'
import StepBackground from './StepBackground4'

// steps
import ChecklistStep from './checklist/ChecklistStep3'
import CompleteStep from 'steps/complete/CompleteStep3'
import CustomHtmlStep from 'steps/custom-html/CustomHtmlStep'
import CustomJSONStep from 'steps/custom-json/CustomJSONStep3'
import FilteringStep from 'steps/filtering/FilteringStep3'
import GalleryStep from 'steps/gallery/GalleryStep'
import GroceryStep from 'steps/grocery/GroceryStep'
import ListicleStep from 'steps/listicle/ListicleStep3'
import PersonalityQuizStep from 'steps/personality-quiz/PersonalityQuizStep3'
import PinterestStep from 'steps/pinterest/PinterestStep3'
import ReferralStep from 'steps/referral/ReferralStep3'
import TextTriviaStep from 'steps/text-trivia/TextTriviaStep'
import UGCPhotoUpload from 'steps/ugc-photo-upload/UGCPhotoUploadStep'
import UGCMediaUpload from 'steps/ugc-media-upload/UGCMediaUploadStep'
import VideoStep from 'steps/video/VideoStep3'
import PollStep from 'steps/poll/PollStep3'
import PuzzleStep from 'steps/puzzle/PuzzleStep'
import LeadGenStep from 'steps/lead-gen/LeadGenStep3'
import ReceiptVerificationStep from 'steps/receipt/ReceiptVerificationStep'
import MemoryMatchStep from 'steps/memory-match/MemoryMatchStep'
import ReviewStep from 'steps/review/ReviewStep3'
import LocatorStep from 'steps/locator/LocatorStep3'
import SpotTheDifferenceStep from 'steps/spot-the-diff/SpotTheDifferenceStep3'
import RecipeMakerStep from 'steps/grocery/genAI/RecipeMakerStep'
import DragAndDrop from 'steps/drag-and-drop/DragAndDrop'
import IncommOfferStep from 'steps/incomm-offer/components/IncommOfferStep'
import TipsAndTricks from 'steps/tips-and-tricks/TipsAndTricks'
import FormStep from './form/FormStep'
import PuckStep from './puck/PuckStep'
import PromoStep from './promo/PromoStep'
import SideBySideVideoStep from './side-by-side-video/SideBySideVideoStep'
import ImageEnhancementStep from './image-enhancement/ImageEnhancementStep'
import ProductScanStep from './product-scan/ProductScanStep'

//This object associates the string assignments made in step.type with the actual .jsx components.
const stepComponents = {
  COMPLETE: CompleteStep,
  CHECKLIST: ChecklistStep,
  CUSTOM_HTML: CustomHtmlStep,
  CUSTOM_JSON: CustomJSONStep,
  FILTERING: FilteringStep,
  GALLERY: GalleryStep,
  GROCERY: GroceryStep,
  LISTICLE: ListicleStep,
  PERSONALITY_QUIZ: PersonalityQuizStep,
  PINTEREST: PinterestStep,
  REFERRAL: ReferralStep,
  TEXT_TRIVIA: TextTriviaStep,
  UGC_PHOTO: UGCPhotoUpload,
  MEDIA_UPLOAD: UGCMediaUpload,
  VIDEO: VideoStep,
  POLL: PollStep,
  PUZZLE: PuzzleStep,
  LEAD_GEN: LeadGenStep,
  RECEIPT_VERIFICATION: ReceiptVerificationStep,
  MEMORY_MATCH: MemoryMatchStep,
  REVIEW: ReviewStep,
  STORE_FINDER: LocatorStep,
  SPOT_DIFFERENCE: SpotTheDifferenceStep,
  DRAG_DROP: DragAndDrop,
  INCOMM: IncommOfferStep,
  GEN_AI_TIPS: TipsAndTricks,
  FORM: FormStep,
  PUCK: PuckStep,
  PROMO: PromoStep,
  SIDE_BY_SIDE_VIDEO: SideBySideVideoStep,
  IMAGE_ENHANCEMENT: ImageEnhancementStep,
  PRODUCT_SCAN: ProductScanStep,
}

// Associates step.type with the API url used to call the start/end services for a given step.
const stepServiceSlugs = {
  COMPLETE: 'complete',
  CHECKLIST: 'checklist',
  CUSTOM_HTML: 'customhtml',
  FILTERING: 'filter',
  GROCERY: 'grocery',
  LISTICLE: 'listicle',
  PERSONALITY_QUIZ: 'quiz',
  PINTEREST: 'pinterest',
  REFERRAL: 'referral',
  TEXT_TRIVIA: 'trivia',
  UGC_PHOTO: 'photoUpload',
  VIDEO: 'video',
  POLL: 'poll',
  PUZZLE: 'puzzle',
  LEAD_GEN: 'lead',
  RECEIPT_VERIFICATION: 'receiptVerification',
  MEMORY_MATCH: 'memory',
  REVIEW: 'review',
  STORE_FINDER: 'location',
  MEDIA_UPLOAD: 'videoUpload',
  CUSTOM_JSON: 'videoUpload',
  GALLERY: 'gallery',
  SPOT_DIFFERENCE: 'spotTheDifference',
  DRAG_DROP: 'drag-and-drop',
  INCOMM: 'incomm-offer',
  GEN_AI_TIPS: 'tips-and-tricks',
  FORM: 'form',
  SIDE_BY_SIDE_VIDEO: 'side-by-side-video',
  IMAGE_ENHANCEMENT: 'image-enhancement',
  PUCK: 'puck',
  PRODUCT_SCAN: 'product-scan',
}

const supportedStepType = stepType => {
  return stepComponents.hasOwnProperty(stepType)
}

const ScreenBlurBlock = () => (
  <div
    style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'black',
    }}
  ></div>
)

const COMPLETE_BY_SKIP = true
const COMPLETE_NOT_SKIPPED = false

const ActionType = {
  STEP_STARTED: 'STEP_STARTED',
  STEP_ENGAGED: 'STEP_ENGAGED',
  STEP_COMPLETED: 'STEP_COMPLETED',
  STEP_SKIPPED: 'STEP_SKIPPED',
  POINTS_AWARDED: 'POINTS_AWARDED',
  BACKGROUND_CHANGED: 'BACKGROUND_CHANGED',
}

const reducer = (state, action) => {
  // always include current action.type in state. allows useEffect to support loading data.
  // anti-pattern? greatest pattern? :D :D :D

  state.currentAction = action.type
  switch (action.type) {
    case ActionType.STEP_STARTED:
      return {
        ...state,
        stepStarted: true,
      }
    case ActionType.STEP_COMPLETED:
      return {
        ...state,
        stepCompleted: true,
      }
    case ActionType.POINTS_AWARDED:
      return {
        ...state,
        pointsAwarded: true,
      }
    case ActionType.STEP_SKIPPED:
      return {
        ...state,
        stepEngaged: true,
        stepCompleted: true,
        skipLinkVisible: false,
        arrowButtonVisible: false,
      }
    case ActionType.STEP_ENGAGED:
      return {
        ...state,
        stepEngaged: true,
        stepCompleted: true,
        skipLinkVisible: false,
        arrowButtonVisible: true,
      }

    case ActionType.BACKGROUND_CHANGED:
      return {
        ...state,
        backgroundImageId: action.backgroundImageId ?? state.backgroundImageId,
        backgroundImageAlt: action.backgroundImageAlt ?? state.backgroundImageAlt,
        backgroundColor: action.backgroundColor ?? state.backgroundColor,
        mobileBackgroundId: action.mobileBackgroundId ?? state.mobileBackgroundId,
        mobileBackgroundImageAlt: action.mobileBackgroundImageAlt ?? state.mobileBackgroundImageAlt,
        backgroundImageSrc: action.backgroundImageSrc ?? state.backgroundImageSrc,
        mobileBackgroundImageSrc: action.mobileBackgroundImageSrc ?? state.mobileBackgroundImageSrc,
        backgroundSize: action.backgroundSize ?? state.backgroundSize,
        backgroundPosition: action.backgroundPosition ?? state.backgroundPosition,
        backgroundRepeat: action.backgroundRepeat ?? state.backgroundRepeat,
        backgroundOpacity: action.backgroundOpacity ?? state.backgroundOpacity,
      }
    default:
      console.warn(`unhandled action.type: ${action.type}`)
    // throw new Error()

    // this break is not configured w/ instant win
  }
}

// isLastStep should be passed in as a prop so we don't need to know about brk
const RenderNextStepButton = (brk, step, state, onArrowClick) => {
  const isLastStep = brk.steps[brk.steps.length - 1].id === step.id

  return (
    <ArrowButton
      isLastStep={isLastStep}
      onClick={onArrowClick}
      visible={state.arrowButtonVisible}
      clickable={true}
    />
  )
}

  const StepHtmlFooter = ({content}) => {

  return content 
    ? <div className={`step-footer`}>
        <div
          className={`step-footer-content`}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    : null
}


const Step4 = ({
  brk,
  step,
  breakId,
  isLocked,
  onGoToNext,
  onStepStart,
  onStepComplete,
  onStepSkip,
  isEmbed,
  onAwardPoints = (stepId, points) => {},
  onAttemptStepStart, // TODO: pass in an event handler/callback instead. this requires component to be aware of logic
  startScreenDisabled,
  handleInstantWinEvent,
  userType,
  onIntersect = (stepId, isIntersecting) => {},
  puckConfig,
}) => {
  const initialState = {
    stepStarted: false,
    stepCompleted: false,
    pointsAwarded: false,
    stepEngaged: false,
    skipLinkVisible: true,
    arrowButtonVisible: step.type === 'CUSTOM_HTML' ? true : false,
    backgroundImageId:
      step.backgroundImageId ||
      (step.backgroundImage && step.backgroundImage.id),
    backgroundImageAlt: (step.backgroundImage && step.backgroundImage.altAttribute) || '',
    backgroundColor: step.backgroundColor,
    mobileBackgroundId:
      step.mobileBackgroundImageId ||
      (step.mobileBackgroundImage && step.mobileBackgroundImage.id),
    mobileBackgroundImageAlt: (step.mobileBackgroundImage && step.mobileBackgroundImage.altAttribute) || '',
  }

  // DEBUG: Log background image alt attribute for analysis
  if (typeof window !== 'undefined' && window.location.search.includes('debugalt')) {
    console.log(`[Step4 ${step.type}] Initial State:`, {
      backgroundImageId: initialState.backgroundImageId,
      backgroundImageAlt: initialState.backgroundImageAlt,
      hasBackgroundImage: !!step.backgroundImage,
      hasAltAttribute: !!step.backgroundImage?.altAttribute,
      altAttribute: step.backgroundImage?.altAttribute,
      mobileAltAttribute: step.mobileBackgroundImage?.altAttribute,
    })
    
    if (!initialState.backgroundImageAlt && initialState.backgroundImageId) {
      console.warn(`[Step4 ${step.type}] ⚠️ Background image found but NO altAttribute!`, {
        backgroundImageId: initialState.backgroundImageId,
        step: step,
      })
    }
    
    if (step.personalityQuiz) {
      console.log(`[Step4 PERSONALITY_QUIZ] Question Background:`, {
        questionBackgroundImageId: step.personalityQuiz.questionBackgroundImageId,
        questionBackgroundImage: step.personalityQuiz.questionBackgroundImage,
        altAttribute: step.personalityQuiz.questionBackgroundImage?.altAttribute,
      })
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState)

  // Some steps (e.g. Product Scan) hold a live camera and need to know when the
  // player scrolls them off-screen so they can pause decoding without tearing
  // the stream down.
  const [isOnScreen, setIsOnScreen] = useState(true)

  // StepService maintains a nonce that the step uses to identify this interaction when it makes calls to awardpoints/complete.
  // We need to maintain the same nonce over the lifetime of a step interaction.
  const service = useRef(
    new StepService(
      stepServiceSlugs[step.type],
      breakId,
      step.id,
      brk.sponsoredType,
      brk.platform,
      step.position,
      step.type,
    ),
  )

  const StepTag =
    step && step.grocery && step.grocery.hasGenAI
      ? RecipeMakerStep
      : step && step.tipsAndTricks
      ? TipsAndTricks
      : step && step.sideBySideVideo
      ? SideBySideVideoStep
      : step && step.imageEnhancement
      ? ImageEnhancementStep
      : step && step.type === 'PROMO' && stepHasPuckContent(step)
      ? PuckStep
      : stepComponents[step.type]

  const handleAwardPoints = async (optionalPoints = 0) => {
    // It's possible for a steps to fire handleAwardPoints() before Step3 has a chance to start them.
    // the API requires a step to be started in order to award points, so we check here
    if (state.stepStarted === false) {
      console.warn('BR-9872345 handleAwardPoints on unstarted step')
      dispatch({
        type: ActionType.STEP_STARTED,
      })

      try {
        const response = await service.current.start()
        if (response.status === 200) {
          const { attempt, firstBreakEngagement } = await response.json()
          onStepStart && onStepStart(step, attempt, firstBreakEngagement)
        } else if (response.status !== UNTRACKED_STATUS) {
          console.warn(
            `BR-34958 call to service.start returns ${response.status}`,
          )
        }
      } catch (e) {
        console.warn(`handleAwardPoints (start)`, e)
      }
    }

    try {
      const response = await service.current.awardPoints(optionalPoints)
      if (response.status === 200) {
        const body = await response.json()
        const { totalPoints } = body
        // if (body.error && (body.error.length > 0)) {
        //   console.warn(`BR-3489587 awardPoints error ${body.error}`)
        // }
        onAwardPoints(totalPoints)
      } else if (response.status !== UNTRACKED_STATUS) {
        console.warn(
          `BR-872354 call to service.awardPoints returns ${response.status}`,
        )
      }
    } catch (e) {
      console.log(`handleAwardPoints`, e)
    }
  }

  const handleIsOnScreen = async isIntersecting => {
    if (isIntersecting === true) {
      if (!step.hasStartScreen && !state.stepStarted) {
        try {
          const response = await service.current.start()
          if (response.status === 200) {
            const { attempt, firstBreakEngagement } = await response.json()
            onStepStart && onStepStart(step, attempt, firstBreakEngagement)
          } else if (response.status !== UNTRACKED_STATUS) {
            console.warn(
              `BR-98124 call to service.start returns ${response.status}`,
            )
          }
        } catch (e) {
          console.warn(`handleIsOnScreen`, e)
        }

        dispatch({
          type: 'STEP_STARTED',
        })
      }
    }

    // not on screen
    if (isIntersecting === false) {
      // user moved on without clicking arrow
      if (state.stepStarted && !state.stepCompleted) {
        if (state.stepEngaged) {
          try {
            await service.current.complete(COMPLETE_NOT_SKIPPED)
          } catch (e) {
            console.log(`handleIsOnScreen awardPoints`, e)
          }
        } else {
          try {
            await service.current.complete(COMPLETE_BY_SKIP)
          } catch (e) {
            console.log(`handleIsOnScreen complete`, e)
          }
        }
        dispatch({
          type: ActionType.STEP_COMPLETED,
        })
      }
    }
    // This is actually triggering the position in the navbar
    onIntersect(step.id, isIntersecting)
  }

  const handleStartSlideClick = async () => {
    console.log('handleStartSlideClick')

    // BR-1469
    // before we actually start, run this up the flag pole to make sure we're ok to start (user is logged in)
    const okToStart = await onAttemptStepStart(step.id)
    console.log(`okToStart: `, okToStart)

    if (!okToStart) {
      // parent said naw. We assume it performs some UI to resolve this situation (like pop a login modal)
      return false
    }

    if (!state.stepStarted) {
      try {
        const response = await service.current.start()
        if (response.status === 200) {
          const { attempt, firstBreakEngagement } = await response.json()
          onStepStart && onStepStart(step, attempt, firstBreakEngagement)
        } else {
          console.warn(
            `BR-871246 call to service.start returns ${response.status}`,
          )
        }
      } catch (e) {
        console.warn(`handleStartSlideClick`, e)
      }

      dispatch({
        type: 'STEP_STARTED',
      })
    }

    // this allows the start slide wrapper to move on
    return true
  }

  // step told us the user engaged
  // remove the skip link and enable the next button
  // mark the step as complete
  const handleEngage = async () => {
    if (state.stepCompleted === false) {
      try {
        // some steps haven't been migrated to use /awardPoints endpoint
        // they'll award points on complete
        await service.current.complete(COMPLETE_NOT_SKIPPED)
      } catch (e) {
        console.log(`handleEngage`, e)
      }
    }

    onStepComplete && onStepComplete(step.id)

    dispatch({
      type: ActionType.STEP_ENGAGED,
    })
  }

  const handleBackgroundChange = async ({
    backgroundImageId,
    backgroundColor,
    mobileBackgroundId,
    backgroundImageAlt,
    mobileBackgroundImageAlt,
    backgroundImageSrc,
    mobileBackgroundImageSrc,
    backgroundSize,
    backgroundPosition,
    backgroundRepeat,
    backgroundOpacity,
  }) => {
    console.log(`changing bg image! ${backgroundImageId}, ${backgroundColor}`)

    dispatch({
      type: ActionType.BACKGROUND_CHANGED,
      backgroundImageId,
      backgroundImageAlt,
      backgroundColor,
      mobileBackgroundId,
      mobileBackgroundImageAlt,
      backgroundImageSrc,
      mobileBackgroundImageSrc,
      backgroundSize,
      backgroundPosition,
      backgroundRepeat,
      backgroundOpacity,
    })
  }

  const handleArrowClick = async () => {
    // console.log(`handleArrowClick`, this)
    onGoToNext && onGoToNext(step.id)
  }

  const handleSkipClick = async () => {
    // console.log('skip click', this)

    if (state.stepCompleted === false) {
      try {
        await service.current.complete(COMPLETE_BY_SKIP)
        // do we care about the response?
      } catch (e) {
        console.warn(e)
      }
    }

    onStepSkip(step.id)
    dispatch({
      type: ActionType.STEP_SKIPPED,
    })
  }

  const getThemeColors = () => {
    try {
      return {
        primaryCopyColor: brk.themeColor2,
        primaryColorButtons: brk.themeColor3,
      }
    } catch (e) {
      console.log(`getThemeColors`, e)
    }
  }

  const SkipButtonComponent = () =>
    step.showSkipButton && (
      <SkipButton
        onClick={handleSkipClick}
        isDisabled={!state.skipLinkVisible}
      />
    )

  const footer = <div className='sponsored-flow-panel--footer sponsored-flow-panel-3--footer'>
    <BreakHTMLFooter content={brk.footerHtml} />
    <StepHtmlFooter content={step.footerHtml} />
    </div>

  return (
    <>
      <IntersectionVisible
        options={{
          threshold: 0.2,
        }}
        onIntersect={isIntersecting => {
          setIsOnScreen(isIntersecting)
          handleIsOnScreen(isIntersecting)
        }}
      >
        {isLocked ? (
          <section className="sponsored-flow-panel step-4">
            <LockedStep
              backgroundImageId={step.startBackgroundImageId || (step.startBackgroundImage && step.startBackgroundImage.id)}
              backgroundColor={step.startBackgroundColor}
              mobileBackgroundId={
                step.mobileStartBackgroundImageId || (step.mobileStartBackgroundImage && step.mobileStartBackgroundImage.id) || step.startBackgroundImageId
              } // older breaks displayed the desktop image on mobile. we need to account for that untl content has an opportunity to fix.
              backgroundImageAlt={(step.startBackgroundImage && step.startBackgroundImage.altAttribute) || ''}
              mobileBackgroundImageAlt={(step.mobileStartBackgroundImage && step.mobileStartBackgroundImage.altAttribute) || ''}
              footer={footer}
            />
          </section>
        ) : (
          <>
            <div
              className={`step-4 sponsored-flow-panel step-3-flow-panel ${
                state && state.arrowButtonVisible
                  ? `engagement-complete`
                  : `engagement-incomplete`
              } sponsored-flow-panel-${step.id}`}
            >
              <StepBackground
                backgroundImageId={state.backgroundImageId}
                backgroundImageAlt={state.backgroundImageAlt}
                backgroundColor={state.backgroundColor}
                mobileBackgroundId={state.mobileBackgroundId}
                mobileBackgroundImageAlt={state.mobileBackgroundImageAlt}
                backgroundImageSrc={state.backgroundImageSrc}
                mobileBackgroundImageSrc={state.mobileBackgroundImageSrc}
                backgroundSize={state.backgroundSize}
                backgroundPosition={state.backgroundPosition}
                backgroundRepeat={state.backgroundRepeat}
                backgroundOpacity={state.backgroundOpacity}
              />
              {StepTag && (
                <StartSlideWrapper
                  startScreenDisabled={startScreenDisabled || puckHasOwnStartScreen(step)}
                  step={step}
                  handleStartSlideClick={handleStartSlideClick}
                  footer={footer}
                  isIntersecting={handleIsOnScreen()}
                >
                  <StepTag
                    brk={brk}
                    step={step}
                    breakId={breakId || brk.id}
                    stepId={step.id}
                    isEmbed={isEmbed} // complete step has different behavior for dailybreak vs embed
                    userType={userType}
                    onStepSkip={onStepSkip}
                    service={service.current}
                    themeColors={getThemeColors()}
                    onEngage={handleEngage}
                    onAwardPoints={handleAwardPoints}
                    onChangeBackground={handleBackgroundChange}
                    isOnScreen={isOnScreen}
                    nextStepButton={RenderNextStepButton(
                      brk,
                      step,
                      state,
                      handleArrowClick,
                    )}
                    skipButton={SkipButtonComponent()}
                    handleInstantWinEvent={handleInstantWinEvent}
                  />
                  { footer }
                </StartSlideWrapper>
              )}
            </div>
          </>
        )}
      </IntersectionVisible>
    </>
  )
}

export { Step4, supportedStepType }
